<template>
  <div v-loading="isLoading">
    <el-row
      v-if="!$hasFeature('customer_intelligence_templates')"
      class="p-bottom-44"
    >
      <el-col :span="24">
        <form-section
          class="intelligence-feature p-top-30 p-bottom-30"
          :body-style="{ padding: '20px' }"
        >
          <div class="bulb-icon p-bottom-18">
            <icon class="icon-lightbulb" glyph="lightbulb" size="36" />
          </div>
          <div class="title p-bottom-24">
            {{ $t('PreMadeTemplatesTab_Intelligence_Title') }}
          </div>
          <div class="description p-bottom-40">
            {{ $t('PreMadeTemplatesTab_Intelligence_Desc') }}
          </div>
          <div>
            <a
              href="https://docs.xtremepush.com/docs/campaign-templates"
              target="_blank"
              style="margin-right: 10px;"
            >
              <el-button
                type="primary"
                plain
                data-qa="pre-made-templates-read-more-btn"
              >
                {{ $t('PreMadeTemplatesTab_Btn_ReadMore') }}
              </el-button>
            </a>
            <a :href="generateMailtoLink">
              <el-button
                type="primary"
                data-qa="pre-made-templates-talk-to-sales-btn"
              >
                <span>{{ $t('PreMadeTemplatesTab_Btn_TalkToSales') }}</span>
                <i class="el-icon-arrow-right el-icon--right" />
              </el-button>
            </a>
          </div>
        </form-section>
      </el-col>
    </el-row>
    <el-row>
      <el-col :xs="9" :sm="9" :md="8" :lg="5" class="p-top-10">
        <div class="menu-title p-bottom-10 p-left-20">
          {{ $t('PreMadeTemplatesTab_MenuTitle') }}
        </div>
        <el-menu class="menu" default-active="0">
          <el-menu-item
            v-for="(name, index) in verticalNames"
            :item="name"
            :index="`${index}`"
            :key="index"
            @click="selectVertical(verticalValues[index])"
          >
            <span slot="title">{{ $t(getVerticalLabel(name)) }}</span>
          </el-menu-item>
        </el-menu>
      </el-col>
      <el-col :xs="15" :sm="15" :md="16" :lg="19">
        <el-row class="search-box">
          <el-input
            :placeholder="$t('PreMadeTemplatesTab_Search_Placeholder')"
            prefix-icon="el-icon-search"
            class="borderless"
            v-model="filter"
          />
        </el-row>
        <el-row>
          <form-section class="templates-box" :body-style="{ padding: '0' }">
            <el-col
              :span="12"
              :key="index"
              class="template-item"
              v-for="(template, index) in filteredTemplates"
            >
              <div class="text-section">
                <div style="width: 100%;">
                  <h4 class="template-title">{{ template.title }}</h4>
                  <div class="template-desc">
                    {{ template.description }}
                  </div>
                </div>
                <div class="template-footer">
                  <el-button
                    type="text"
                    v-if="
                      $can('viewProjectItem') &&
                        (!$hasFeature('customer_intelligence_templates') ||
                          !template.canCreate)
                    "
                    @click="$router.push(template.previewUrl)"
                    data-qa="pre-made-templates-preview-btn"
                  >
                    <span>{{ $t('PreMadeTemplatesTab_Btn_Preview') }}</span>
                    <i class="el-icon-right" />
                  </el-button>
                  <el-button
                    type="text"
                    v-if="!template.canCreate && $can('viewProjectItem')"
                    @click="whyCantIUse(template)"
                    data-qa="pre-made-templates-why-not-btn"
                  >
                    <i class="el-icon-info" />
                    <span>{{ $t('PreMadeTemplatesTab_Btn_WhyNot') }}</span>
                  </el-button>
                  <template>
                    <el-button
                      type="text"
                      v-if="
                        $can('createProjectItem') &&
                          template.title &&
                          template.canCreate &&
                          canUseTemplateCampaign(template)
                      "
                      @click="$router.push(template.copyUrl)"
                      data-qa="pre-made-templates-copy-btn"
                    >
                      <i class="el-icon-copy-document" />
                      <span class="m-left-1">
                        {{ $t('PreMadeTemplatesTab_Btn_Copy') }}
                      </span>
                    </el-button>
                    <el-button
                      type="text"
                      v-if="
                        $can('createProjectItem') &&
                          template.title &&
                          template.canCreate &&
                          canUseTemplateCampaign(template)
                      "
                      @click="$router.push(template.createUrl)"
                      data-qa="pre-made-templates-create-campaign-btn"
                    >
                      <icon
                        glyph="paper-plane-2"
                        style="margin-bottom: -2px;"
                      />
                      <span class="m-left-1">
                        {{ $t('PreMadeTemplatesTab_Btn_CreateCampaign') }}
                      </span>
                    </el-button>
                  </template>
                  <el-button
                    type="text"
                    v-if="$can('CampaignTemplate:PublisherAccess')"
                    @click="deleteTemplate(template.id)"
                    class="delete-btn"
                    data-qa="pre-made-templates-delete-btn"
                  >
                    <i class="el-icon-delete" />
                    <span class="m-left-1">
                      {{ $t('PreMadeTemplatesTab_Btn_Delete') }}
                    </span>
                  </el-button>
                </div>
              </div>
              <div class="tag-section">
                <el-tag
                  v-if="
                    template.flags.includes('customer_intelligence_templates')
                  "
                  size="mini"
                  class="intelligence-tag"
                >
                  <icon class="icon p-right-5" glyph="lightbulb" />
                  <span>
                    {{ $t('PreMadeTemplatesTab_Tag_Intelligence') }}
                  </span>
                </el-tag>
              </div>
            </el-col>
            <div v-if="filteredTemplates.length === 0" class="no-templates">
              {{ $t('PreMadeTemplatesTab_NoTemplatesFound') }}
            </div>
          </form-section>
        </el-row>
      </el-col>
    </el-row>
    <missing-features-dialog
      :flags="missingFlags"
      @close="closeDialog()"
      v-if="showMissingFeatures"
    />
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import FormSection from '@/common/components/form/FormSection.vue'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
import MissingFeaturesDialog from '@/modules/campaigns/components/MissingFeaturesDialog.vue'
import { isCampaignAccessible } from '@/modules/campaigns/helpers/CampaignPermissions'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'PreMadeTemplatesTab',
  components: { MissingFeaturesDialog, FormSection },
  data() {
    return {
      filter: null,
      isLoading: false,
      templates: [],
      verticals: {},
      selectedVertical: 0,
      showMissingFeatures: false,
      missingFlags: []
    }
  },
  mounted() {
    this.isLoading = true
    Promise.all([
      new RestApiPaginator({
        method: 'get',
        url: 'shared-campaign-templates',
        params: {
          project_id: this.$route.query.project_id,
          expand: 'flags,missingChannels,missingFeatures'
        }
      })
        .fetchAllPages()
        .then(data => {
          this.templates = data.map(template => {
            template.title = this.$t(template.title)
            template.description = this.$t(template.description)
            template.previewUrl = `/push/campaign/template/view?template=${template.id}&is_shared=1&project_id=${this.$route.query.project_id}`
            template.copyUrl = `/push/campaign/template/copy?template=${template.id}&is_shared=1&project_id=${this.$route.query.project_id}`
            template.createUrl = `/push/campaign/template/create-from?template=${template.id}&is_shared=1&project_id=${this.$route.query.project_id}`
            template.canCreate =
              this.isEmpty(template.missingChannels) &&
              this.isEmpty(template.missingFeatures)
            return template
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        }),
      RestApi.get('/verticals')
        .then(({ data }) => {
          this.verticals = {
            VERTICAL_ALL: 0,
            ...data
          }
        })
        .catch(error => {
          this.$message({
            message: error,
            type: 'error'
          })
        })
    ]).finally(() => {
      this.isLoading = false
    })
  },
  computed: {
    verticalNames() {
      return Object.keys(this.verticals)
    },

    verticalValues() {
      return this.verticalNames.map(name => this.verticals[name])
    },

    filteredTemplates() {
      if (this.selectedVertical === 0 && !this.filter) {
        return this.templates
      }

      let templates = this.templates.filter(template => {
        if (this.selectedVertical === 0) {
          return template.verticals
        }
        return template.verticals & this.selectedVertical
      })

      if (!this.filter) {
        return templates
      }

      return templates.filter(template =>
        this.filter
          .toLowerCase()
          .split(' ')
          .map(searchTerm =>
            (template.title + ' ' + template.description)
              .toLowerCase()
              .includes(searchTerm)
          )
          .reduce((val1, val2) => val1 && val2)
      )
    },
    generateMailtoLink() {
      return `mailto:account-management-team@xtremepush.com?subject=Pre-made templates feature request (${this.$store.state.app.project.name})`
    },
    isMaxautomationEnabled() {
      return $hasFeature('maxautomation')
    },
    isNewMxaEnabled() {
      return $hasFeature('newMxa')
    },
    isLegacyMxaEnabled() {
      return $hasFeature('legacyMxaEnabled')
    }
  },
  methods: {
    selectVertical(verticalBitmask) {
      this.selectedVertical = verticalBitmask
    },
    getVerticalLabel(vertical) {
      const verticals = {
        VERTICAL_ALL: 'PreMadeTemplatesTab_Vertical_All',
        VERTICAL_ECOMMERCE_RETAIL:
          'PreMadeTemplatesTab_Vertical_Ecommerce_Retail',
        VERTICAL_OTHER: 'PreMadeTemplatesTab_Vertical_Other',
        VERTICAL_PUBLISHING_MEDIA:
          'PreMadeTemplatesTab_Vertical_Publishing_Media',
        VERTICAL_RETAIL_BANKING_INSURANCE:
          'PreMadeTemplatesTab_Vertical_Retail_Banking_Insurance',
        VERTICAL_SPORTS_BETTING_GAMING:
          'PreMadeTemplatesTab_Vertical_Betting_Gaming',
        VERTICAL_TRAVEL_TRANSPORT:
          'PreMadeTemplatesTab_Vertical_Travel_Transport'
      }

      return verticals[vertical]
    },
    isEmpty(obj) {
      return Object.keys(obj).length === 0
    },
    closeDialog() {
      this.showMissingFeatures = false
      this.missingFlags = []
    },
    whyCantIUse(template) {
      this.missingFlags.push(...template.missingChannels)
      this.missingFlags.push(...template.missingFeatures)
      this.showMissingFeatures = true
    },
    deleteTemplate(templateId) {
      this.$msgbox({
        title: this.$t('PreMadeTemplatesTab_DialogDelete_Title'),
        message: this.$t('PreMadeTemplatesTab_DialogDelete_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('PreMadeTemplatesTab_Button_DeleteTemplate'),
        cancelButtonText: this.$t('PreMadeTemplatesTab_Button_Cancel'),
        center: true
      }).then(() => {
        RestApi({
          method: 'delete',
          url: `shared-campaign-templates/${templateId}`,
          params: {}
        })
          .then(() => {
            this.$message({
              message: this.$t('PreMadeTemplatesTab_Message_DeleteSuccess'),
              type: 'success'
            })
            this.templates = this.templates.filter(
              template => template.id !== templateId
            )
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      })
    },
    canUseTemplateCampaign(templateCampaign) {
      if (
        !templateCampaign ||
        !templateCampaign.config ||
        !templateCampaign.config.campaign
      ) {
        return true
      }

      return isCampaignAccessible(templateCampaign.config.campaign, {
        maxautomation: this.isMaxautomationEnabled,
        legacyMxaEnabled: this.isLegacyMxaEnabled,
        newMxa: this.isNewMxaEnabled
      })
    }
  }
}
</script>

<style scoped lang="scss">
.icon-lightbulb {
  color: var(--ep-primary-600, #176ca1);
}

.intelligence-feature {
  text-align: center;

  .title {
    font-size: 24px;
    color: var(--ep-primary-400, #1d87c9);
  }

  .description {
    font-size: 14px;
    color: #7e8596;
  }
}

.menu-title {
  color: #5c6374;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
}

.menu {
  border: none;

  /deep/ .el-menu-item {
    height: 42px;
    line-height: 42px;
  }

  /deep/ .el-submenu__title {
    height: 42px;
    line-height: 42px;
    font-weight: 600;
    font-size: 15px;
    color: #4b515f;
  }

  /deep/ .el-submenu,
  /deep/ .el-menu-item {
    line-height: 42px;
    font-weight: 600;
    font-size: 14px;
    color: #5c6374;
  }

  /deep/ .el-menu-item.is-active {
    color: var(--ep-primary-400, #1d87c9);
  }

  /deep/ .el-menu-item:hover,
  /deep/ .el-menu-item:focus,
  /deep/ .el-submenu__title:hover {
    background-color: var(--ep-primary-50, #e5f2fb);
    border-radius: 4px;
  }
}

/deep/ .borderless input {
  border: none;
}

.templates-box {
  border: 1px solid #dbdee7;
  box-shadow: 0 4px 8px rgba(75, 81, 95, 0.05);
  border-radius: 4px;

  .no-templates {
    color: #5c6374;
    text-align: center;
  }

  /deep/ &.form-section {
    border: none;
    box-shadow: none;
  }

  .template-item {
    border: 1px solid #ebeef5;
    border-top: 0;
    padding: 20px 20px 8px;
    flex: 1;
    display: inline-flex;
    flex-direction: row;
    justify-content: space-between;
    height: 124px;

    &:nth-child(even) {
      border-left: 0;
    }

    // select first two children
    &:nth-child(-n + 2) {
      border-top: 1px solid #ebeef5;
    }

    &:last-child,
      // select last odd element
    &:nth-child(odd):nth-last-child(-n + 2) {
      border-right: 1px solid #ebeef5;
    }

    &:hover {
      box-shadow: 0 4px 8px rgba(75, 81, 95, 0.05);
    }

    .text-section {
      width: calc(100% - 140px);
      padding-left: 10px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;

      .template-title {
        font-weight: 600;
        font-size: 14px;
        color: #4b515f;
        margin-bottom: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .template-desc {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        -webkit-line-clamp: 3;
        font-size: 11px;
        color: #7e8596;
      }

      .template-footer {
        display: flex;
        visibility: hidden;

        button {
          font-size: 12px;
          line-height: 12px;
          color: var(--ep-primary-400, #1d87c9);
        }

        .delete-btn {
          color: #f78989;
        }
      }

      &:hover .template-footer {
        visibility: visible;
      }
    }
    .tag-section {
      padding-right: 13px;

      .intelligence-tag {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: var(--ep-primary-50, #e5f2fb);
        color: var(--ep-primary-600, #176ca1);
        border: none;
        font-weight: 600;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaigns/components/PreMadeTemplatesTab.vue